import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { FaBuilding } from "react-icons/fa";
import { ChakraIcon } from "./ChakraIcon";
import { showToast } from "../utils/toastUtils";

const CreateOrganization: React.FC = () => {
  const { createOrganization } = useAuth();
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleCreateOrganization = async () => {
    if (!orgName.trim()) {
      showToast({
        title: "Error",
        description: "Organization name is required.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (orgName.length < 3) {
      showToast({
        title: "Error",
        description: "Organization name must be at least 3 characters long.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await createOrganization(orgName, navigate);
      showToast({
        title: "Success",
        description: "Organization created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      showToast({
        title: "Error",
        description: error.message || "Failed to create organization.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Large Floating Circles */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-[#FBD28222] to-[#B5914322] rounded-full opacity-40 animate-pulse"></div>
        <div className="absolute top-40 right-32 w-24 h-24 bg-gradient-to-br from-[#B5914320] to-[#FBD28220] rounded-full opacity-35 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-32 w-40 h-40 bg-gradient-to-br from-[#FBD28218] to-[#B5914318] rounded-full opacity-30 animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(251, 210, 130, 0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(251, 210, 130, 0.08) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      {/* Main Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-xl border border-[#2E2F33] bg-[#0F1014] p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#FBD282] to-[#B59143] mb-4">
              <ChakraIcon as={FaBuilding} boxSize={8} color="#0B0B0F" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              <span className="bg-clip-text text-transparent" style={{
                background: "linear-gradient(90deg, #FBD282, #B59143)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>Create Organization</span>
            </h1>
            <p className="text-sm text-[#C3C9D3]">
              Set up your organization to get started
            </p>
          </div>

          {/* Form */}
          <div className="space-y-6">
            <div>
              <label htmlFor="orgName" className="block text-sm font-medium text-[#C3C9D3] mb-2">
                Organization Name <span className="text-[#B59143]">*</span>
              </label>
              <input
                id="orgName"
                type="text"
                placeholder="Enter your organization name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !loading && orgName.trim().length >= 3) {
                    handleCreateOrganization();
                  }
                }}
                className={`w-full px-4 py-3 bg-[#1A1C1F] border rounded-lg text-white placeholder-[#6B7280] focus:outline-none focus:ring-2 transition-all ${
                  orgName.length > 0 && orgName.length < 3
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                    : "border-[#2E2F33] focus:border-[#B59143] focus:ring-[#B59143]/20"
                }`}
                disabled={loading}
              />
              {orgName.length > 0 && orgName.length < 3 && (
                <p className="mt-1 text-xs text-red-400">
                  Organization name must be at least 3 characters long
                </p>
              )}
            </div>

            <button
              onClick={handleCreateOrganization}
              disabled={loading || orgName.trim().length < 3}
              className={`w-full px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 ${
                loading || orgName.trim().length < 3
                  ? "bg-[#2E2F33] text-[#6B7280] cursor-not-allowed"
                  : "bg-gradient-to-r from-[#FBD282] to-[#B59143] hover:from-[#FBD282]/90 hover:to-[#B59143]/90 hover:shadow-[0_0_20px_rgba(181,145,67,0.4)] text-[#0B0B0F]"
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                "Create Organization"
              )}
            </button>

            {/* Info Text */}
            <p className="text-xs text-center text-[#6B7280] mt-4">
              Your organization will be created with default settings. You can customize it later.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
